<script setup lang="ts">
// Time-series OHLC candlestick. One row = one candle with open/high/
// low/close columns. Up candles use `--lucent-candlestick-up`
// (defaults to `--lucent-chart-up`); down candles use the down token.

import { computed } from "vue";

import { resolveChartHeight } from "./chart-heights";
import VChart from "vue-echarts";
import "../echarts.ts";

import type { CandlestickChartSpec } from "../sbi/protocol";
import { CHART_COLORS } from "../theme.ts";
import { baseOption, resolveCssVar } from "./echarts-base";
import { dataZoomOption, plotWidthEstimate } from "./chart-layout";
import { useChartLayout } from "./use-chart-layout";

// The chart's own width / print / touch (see chart-layout.ts).
const { chartRef, ctx } = useChartLayout();

// Below this many px per candle the bodies collapse to slivers.
const MIN_CANDLE_PX = 4;

const props = withDefaults(
  defineProps<{
    spec: CandlestickChartSpec;
    formats?: Record<string, string>;
  }>(),
  { formats: () => ({}) },
);

const emit = defineEmits<{
  (e: "click-target", id: string, datum: Record<string, unknown>): void;
}>();

const rows = computed<ReadonlyArray<Record<string, unknown>>>(() =>
  Array.isArray(props.spec.data) ? props.spec.data : [],
);

// Up / down colours — embedder token first (`--lucent-candlestick-*`
// then `--lucent-chart-*` as a secondary token), then canvas-safe hex
// fallback. Canvas can't resolve `var(--…)`, so without this the
// candles paint with an invalid fill and the chart renders empty.
const upColor = computed(
  () =>
    resolveCssVar("var(--lucent-candlestick-up)") ??
    resolveCssVar("var(--lucent-chart-up)") ??
    CHART_COLORS.candlestickUp,
);
const downColor = computed(
  () =>
    resolveCssVar("var(--lucent-candlestick-down)") ??
    resolveCssVar("var(--lucent-chart-down)") ??
    CHART_COLORS.candlestickDown,
);

const option = computed(() => {
  const base = baseOption({ title: props.spec.title }, ctx.value);
  const xs = rows.value.map((r) => String(r[props.spec.x] ?? ""));
  // ECharts candlestick expects [open, close, low, high] — note the order.
  const data = rows.value.map((r) => [
    Number(r[props.spec.open]),
    Number(r[props.spec.close]),
    Number(r[props.spec.low]),
    Number(r[props.spec.high]),
  ]);
  // A long series on a narrow chart shrinks candles to sub-pixel
  // slivers. Open on the most recent candles at a legible width and
  // let the reader zoom out with a slider. Slider only: the author never
  // asked for zoom, and `inside` would capture the mouse wheel over the
  // chart, so scrolling the page past it would zoom instead. Print can't
  // zoom — it keeps every candle at a 1px minimum instead.
  const plot = plotWidthEstimate(ctx.value.width);
  const crowded = !ctx.value.print && plot > 0 && plot / Math.max(1, xs.length) < MIN_CANDLE_PX;
  const zoom = crowded
    ? dataZoomOption("slider", ctx.value, {
        startValue: Math.max(0, xs.length - Math.floor(plot / 6)),
        endValue: Math.max(0, xs.length - 1),
      })
    : { reserveBottom: 0, dataZoom: undefined };
  return {
    ...base,
    grid: { ...base.grid, bottom: base.grid.bottom + zoom.reserveBottom },
    xAxis: {
      ...base.xAxis,
      type: "category" as const,
      data: xs,
      axisLabel: { ...base.xAxis.axisLabel, hideOverlap: true },
    },
    yAxis: { ...base.yAxis, type: "value" as const, scale: true },
    // Spread `base.tooltip` so the mono textStyle + axisPointer travel.
    tooltip: { ...base.tooltip },
    dataZoom: zoom.dataZoom,
    series: [
      {
        type: "candlestick" as const,
        data,
        ...(ctx.value.print ? { barMinWidth: 1 } : {}),
        itemStyle: {
          color: upColor.value,
          color0: downColor.value,
          borderColor: upColor.value,
          borderColor0: downColor.value,
        },
      },
    ],
  };
});

const height = computed(() =>
  resolveChartHeight("candlestick", props.spec.height),
);

function onClick(p: { dataIndex?: number; data?: unknown }) {
  if (!props.spec.id || typeof p?.dataIndex !== "number") return;
  const row = rows.value[p.dataIndex];
  if (!row) return;
  emit("click-target", props.spec.id, {
    x: row[props.spec.x],
    open: Number(row[props.spec.open]),
    high: Number(row[props.spec.high]),
    low: Number(row[props.spec.low]),
    close: Number(row[props.spec.close]),
  });
}
</script>

<template>
  <v-chart
    ref="chartRef"
    class="chart"
    :style="{ height: `${height}px` }"
    :option="option"
    :init-options="{ renderer: 'canvas' }"
    autoresize
    @click="onClick"
  />
</template>

<style scoped>
.chart {
  width: 100%;
}
</style>
